import React, { useId, useState } from 'react';

type BaseProps = {
  /** Visible field label. */
  label: string;
  /** Optional helper text under the field. */
  hint?: React.ReactNode;
  /** Error message — when set, the field is styled invalid and the text shown. */
  errorMessage?: string;
  /** Render as a multi-line textarea instead of a single-line input. */
  multiline?: boolean;
  /** Stretch to container width. Default true. */
  fullWidth?: boolean;
  /** Wrapper style override. */
  containerStyle?: React.CSSProperties;
};

type InputOnlyProps = BaseProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style'> & {
    multiline?: false;
    /** Control style override. */
    style?: React.CSSProperties;
  };

type TextareaOnlyProps = BaseProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'> & {
    multiline: true;
    /** Control style override. */
    style?: React.CSSProperties;
  };

export type InputFieldProps = InputOnlyProps | TextareaOnlyProps;

/**
 * InputField — label + native input/textarea wired to DS tokens.
 * Gives a calm focus ring (--focus-ring), invalid state from errorMessage,
 * and field-sized (≥touch-min) controls. Use multiline for textareas.
 */
export function InputField(props: InputFieldProps) {
  const {
    label,
    hint,
    errorMessage,
    multiline,
    fullWidth = true,
    containerStyle,
    style,
    id,
    ...controlProps
  } = props as InputFieldProps & { id?: string };

  const reactId = useId();
  const fieldId = id ?? `inputfield-${reactId}`;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = errorMessage ? `${fieldId}-error` : undefined;
  const [focused, setFocused] = useState(false);

  const invalid = Boolean(errorMessage);
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  const controlStyle: React.CSSProperties = {
    width: fullWidth ? '100%' : undefined,
    minHeight: multiline ? undefined : 'var(--touch-min)',
    padding: multiline ? 'var(--space-3) var(--space-4)' : '0 var(--space-4)',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-body)',
    fontWeight: 'var(--weight-regular)',
    lineHeight: multiline ? 'var(--leading-normal)' : 1.2,
    color: 'var(--text-primary)',
    background: 'var(--surface-card)',
    border: `var(--border-width-strong) solid ${
      invalid ? 'var(--status-deny-bar)' : focused ? 'var(--action-primary)' : 'var(--border-strong)'
    }`,
    borderRadius: 'var(--radius-md)',
    boxShadow: focused ? 'var(--focus-ring)' : 'none',
    outline: 'none',
    transition: 'border-color .12s ease, box-shadow .12s ease',
    resize: multiline ? 'vertical' : undefined,
    ...style,
  };

  const sharedProps = {
    id: fieldId,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    onFocus: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      setFocused(true);
      (controlProps as any).onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      setFocused(false);
      (controlProps as any).onBlur?.(e);
    },
    style: controlStyle,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        width: fullWidth ? '100%' : undefined,
        ...containerStyle,
      }}
    >
      <label
        htmlFor={fieldId}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-label)',
          fontWeight: 'var(--weight-semibold)',
          lineHeight: 'var(--leading-snug)',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
      </label>

      {multiline ? (
        <textarea
          {...(controlProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          {...sharedProps}
          rows={(controlProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>).rows ?? 4}
        />
      ) : (
        <input {...(controlProps as React.InputHTMLAttributes<HTMLInputElement>)} {...sharedProps} />
      )}

      {errorMessage ? (
        <span
          id={errorId}
          role="alert"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-caption)',
            lineHeight: 'var(--leading-snug)',
            color: 'var(--status-deny)',
          }}
        >
          {errorMessage}
        </span>
      ) : hint ? (
        <span
          id={hintId}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-caption)',
            lineHeight: 'var(--leading-snug)',
            color: 'var(--text-muted)',
          }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}
